import { useCallback, useMemo, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import {
  useGetTouchpointContentTypesQuery,
  useClassTypeOptions,
  usePopulationTypeOptions,
} from "hooks/api/dictionary";
import { ContentTypeDictionaryItem } from "hooks/api/dictionary/dictionary";
import { useClientCampaigns } from "hooks/use-client-campaigns";
import {
  useSetTouchpointDetailCache,
  type TouchpointAttributes,
} from "hooks/api/use-touchpoints";
import { getTouchpoint } from "generated/api/touchpoint";
import type {
  TouchpointUpdateRequest,
  TouchpointUpdateRequestContentType,
  TouchpointUpdateRequestPopulationType,
  TouchpointUpdateRequestType,
} from "generated/schemas";
import { mapTouchpointDetail } from "utilities/api-mappers";
import { PaginatedRequestOptions } from "types/pagination";

const touchpointApi = getTouchpoint();

const CAMPAIGN_REQUEST_OPTIONS: PaginatedRequestOptions = { unpaged: true };

import { getErrorMessage } from "utilities";
import { getTouchpointPaths } from "utilities/app-routes";
import { formatTouchpointClassesForApi } from "components/modals/creative-modal/creative-modal-utils";

import { LoadingButtonState } from "ui/button";
import { OptionType } from "ui/select";
import {
  EditEmailFormData,
  toFormPopulationType,
} from "components/modals/creative-modal/creative-modal-types";

import { EDIT_EMAIL_FORM_VALIDATION_SCHEMA } from "components/modals/creative-modal/creative-modal-consts";

export const useEditEmail = (
  isOpen: boolean,
  touchpoint: TouchpointAttributes,
  onClose?: () => void,
  onConfirmClose?: () => void,
) => {
  const navigate = useNavigate();
  const currentClient = useCurrentClient();
  const defaultCampaign = useCurrentCampaign();
  const setTouchpointDetailCache = useSetTouchpointDetailCache();

  const [isFormValid, setIsFormValid] = useState(false);
  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const defaultValues: EditEmailFormData = useMemo(
    () => ({
      name: touchpoint.name || "",
      populationType: toFormPopulationType(
        touchpoint.populationType,
      ),
      contentType: touchpoint.contentType || "",
      campaignId: touchpoint.campaignId || "",
      copyCode: touchpoint.copyCode || "",
      folderId: touchpoint.folderId || "",
      insightsActivator: touchpoint.insightsActivator,
      classes: touchpoint.classes ?? [],
    }),
    [touchpoint],
  );

  const methods = useForm<EditEmailFormData>({
    mode: "onChange",
    defaultValues: defaultValues,
    resolver: EDIT_EMAIL_FORM_VALIDATION_SCHEMA,
  });

  const {
    reset,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = methods;

  const clientCampaigns = useClientCampaigns(CAMPAIGN_REQUEST_OPTIONS, {
    delayFetch: false,
    trigger: true,
  });

  const selectedCampaignId = watch("campaignId");
  const currentContentType = watch("contentType");

  const prevCampaignIdRef = useRef(selectedCampaignId);

  // Clear folderId when campaignId changes
  useEffect(() => {
    if (prevCampaignIdRef.current !== selectedCampaignId) {
      setValue("folderId", "", { shouldValidate: true });
    }
    prevCampaignIdRef.current = selectedCampaignId;
  }, [selectedCampaignId, setValue]);

  // Find the selected campaign from the list
  const selectedCampaign = useMemo(() => {
    if (selectedCampaignId && clientCampaigns.length > 0) {
      const found = clientCampaigns.find((campaign) => campaign.id === selectedCampaignId);
      if (found) return found;
    }
    return defaultCampaign;
  }, [selectedCampaignId, clientCampaigns, defaultCampaign]);

  const campaignObjective = selectedCampaign.campaignObjective || defaultCampaign.campaignObjective;

  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery(campaignObjective);
  const { classTypeOptions } = useClassTypeOptions(campaignObjective);
  const { populationTypeOptions } = usePopulationTypeOptions();

  const contentTypeOptions: OptionType[] = useMemo(() => {
    return contentTypes.map((type) => ({
      label: type.description,
      value: type.name,
    }));
  }, [contentTypes]);

  // Handle content type validation and auto-setting when content types change
  useEffect(() => {
    if (!contentTypes?.length) return;
    if (clientCampaigns.length === 0) return;

    const isCurrentContentTypeValid =
      currentContentType &&
      contentTypes.some((type: ContentTypeDictionaryItem) => type.name === currentContentType);

    // If current value is invalid or not set, handle it
    if (!currentContentType || !isCurrentContentTypeValid) {
      if (contentTypes.length === 1) {
        // Auto-set if only one option
        setValue("contentType", contentTypes[0].name, {
          shouldValidate: true,
        });
      } else if (!isCurrentContentTypeValid && currentContentType) {
        // Clear invalid contentType
        setValue("contentType", "", {
          shouldValidate: true,
        });
      }
      // If no contentType and multiple options, let user select
    }
  }, [contentTypes, currentContentType, campaignObjective, clientCampaigns.length, setValue]);

  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid, isDirty]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      reset(defaultValues);
      setSubmitState(LoadingButtonState.Default);
    }
    // eslint-disable-next-line
  }, [isOpen, reset]);

  // Close modal on successful submit (when not navigating)
  useEffect(() => {
    if (submitState === LoadingButtonState.Success) {
      if (onClose) {
        onClose();
      }
    }
  }, [submitState, onClose]);

  const handleCancelClick = () => {
    if (isDirty && onConfirmClose) {
      onConfirmClose();
    } else if (onClose) {
      onClose();
    }
  };

  const handleEditEmailSubmit = useCallback(
    (data: EditEmailFormData) => {
      setSubmitState(LoadingButtonState.Loading);

      const campaignIdForApi = data.campaignId || defaultCampaign.id;

      const payload: TouchpointUpdateRequest = {
        name: data.name,
        type: touchpoint.type as unknown as TouchpointUpdateRequestType,
        contentType: data.contentType as TouchpointUpdateRequestContentType,
        populationType: data.populationType as TouchpointUpdateRequestPopulationType,
        copyCode: data.copyCode,
        folderId: data.folderId || undefined,
        insightsActivator: data.insightsActivator,
        campaignId: campaignIdForApi,
        classes: formatTouchpointClassesForApi(data.classes),
      };

      touchpointApi
        .updateTouchpoint(
          {
            clientId: currentClient.id,
            campaignId: campaignIdForApi,
            id: touchpoint.id,
          },
          payload,
        )
        .then((updated) => {
          const attributes = mapTouchpointDetail(
            updated,
            currentClient.id,
            (updated.campaignId as string) || campaignIdForApi,
          );
          setTouchpointDetailCache(
            {
              clientId: currentClient.id,
              campaignId: attributes.campaignId,
            },
            attributes,
          );

          toast.success({
            data: {
              title: "Updated email",
            },
          });

          setSubmitState(LoadingButtonState.Success);

          // If campaign changed, navigate to new campaign path
          if (data.campaignId && defaultCampaign.id !== data.campaignId) {
            const { touchpointDetailsPath } = getTouchpointPaths({
              clientId: currentClient.id,
              campaignId: data.campaignId,
              touchpointId: touchpoint.id,
            });

            navigate(touchpointDetailsPath);
          }
        })
        .catch((err) => {
          toast.error({
            data: {
              title: "Failed to edit email",
              message: getErrorMessage(err?.response?.data),
            },
          });
          setSubmitState(LoadingButtonState.Default);
        });
    },
    [
      touchpoint,
      currentClient.id,
      defaultCampaign.id,
      navigate,
      setTouchpointDetailCache,
    ],
  );

  return {
    methods,
    isFormValid,
    submitState,
    handleEditEmailSubmit,
    handleCancelClick,
    contentTypes,
    contentTypeOptions,
    clientCampaigns,
    clientId: currentClient.id,
    campaignId: defaultCampaign.id,
    selectedCampaignId,
    classTypeOptions,
    populationTypeOptions,
  };
};
